<template>
    <div class="quickpay-payment-overview">
        <QuickpayPaymentHeader
            :companyName="data.corporation.name"
            :amount="displayAmount"
            :currency="data.document.currency"
            :logoUrl="data.corporation.logoUrl"
            :hideAmount="data.document.hide_amount_due"
        />
        <QuickpayDownloadDocument
            :docType="data.document.doctype"
            :docId="data.document.id"
            :corpId="data.document.corpid"
            :pdfUrl="data.document.pdf_link"
            :documentNumber="data.document.number"
            :documentStep="data.document.step"
            :expired="data.document.expired"
            class="quickpay-payment-overview__download-document"
        />
        <CompassPlainButton
            v-if="hasDeadlines"
            class="quickpay-payment-overview__change-deadline"
            :label="$t.__('quickpay.QuickpayPaymentOverview.changeDeadline')"
            leftIcon="caret-left"
            :variant="CompassEnums.PlainButtonVariantType.NEUTRAL"
            @click="handleChangeDeadline"
        />
        <QuickpayMarketingPreferences
            v-if="data.massmailing"
            :massmailing="data.massmailing"
            :hash="hash"
        />
        <QuickpayCGV
            v-if="data.condition"
            :condition="data.condition"
            :checked="cgvAccepted"
            :hasError="cgvError"
            @update:checked="handleCgvChange"
        />
        <CompassAlert
            v-if="paymentProviderError"
            :type="CompassEnums.AlertType.ERROR"
            :description="paymentProviderError"
            :showClose="false"
            class="quickpay-payment-overview__provider-error"
        />
        <div class="quickpay-payment-overview__cards">
            <template v-if="loading">
                <div
                    v-for="index in 3"
                    :key="`skeleton-${index}`"
                    :class="[
                        'quickpay-payment-overview__skeleton-card',
                        { 'quickpay-payment-overview__skeleton-card--first': index === 1 },
                    ]"
                >
                    <CompassSkeleton :variant="CompassEnums.SkeletonVariantType.LINE" :size="CompassEnums.SkeletonSize.LARGE" />
                </div>
            </template>
            <template v-else-if="hasError || availablePaymentMethods.length === 0">
                <CompassAlert
                    :type="CompassEnums.AlertType.ERROR"
                    :description="$t.__('quickpay.QuickpayPaymentOverview.payment_methods_error')"
                    :showIcon="false"
                />
            </template>
            <template v-else>
                <div v-for="paymentMethod in availablePaymentMethods" :key="paymentMethod.type">
                    <QuickpayPaymentCard
                        :type="paymentMethod.type"
                        :isSelected="selectedPaymentMethod === paymentMethod.type"
                        :data="paymentMethod.data"
                        @select="handleSelectPaymentMethod"
                        @pay="handlePaymentClick"
                    />
                    <form
                        v-if="paymentMethod.type === 'paypal'"
                        ref="paypalForm"
                        :action="paymentMethod.data.form_action"
                        method="post"
                        style="display: none"
                    >
                        <input type="hidden" name="cmd" value="_express-checkout" />
                        <input type="hidden" name="token" :value="paymentMethod.data.token" />
                    </form>
                    <form
                        v-else-if="paymentMethod.type === 'atos'"
                        ref="atosForm"
                        :action="paymentMethod.data.form_action"
                        method="post"
                        style="display: none"
                    >
                        <input type="hidden" name="Data" :value="paymentMethod.data.data" />
                        <input type="hidden" name="InterfaceVersion" value="HP_2.9" />
                        <input type="hidden" name="Seal" :value="paymentMethod.data.seal" />
                    </form>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import QuickpayPaymentHeader from 'vcomponents/quickpay/QuickpayPaymentHeader.vue';
import QuickpayCGV from 'vcomponents/quickpay/QuickpayCGV.vue';
import QuickpayMarketingPreferences from 'vcomponents/quickpay/QuickpayMarketingPreferences.vue';
import QuickpayPaymentCard from 'vcomponents/quickpay/payment/QuickpayPaymentCard.vue';
import QuickpayDownloadDocument from 'vcomponents/quickpay/QuickpayDownloadDocument.vue';

const PAYMENT_METHODS = ['stancer', 'gocardless', 'stripe', 'paypal', 'adyenPayByLink', 'atos'];

export default {
    components: {
        QuickpayPaymentCard,
        QuickpayPaymentHeader,
        QuickpayCGV,
        QuickpayMarketingPreferences,
        QuickpayDownloadDocument,
    },
    props: {
        data: {
            type: Object,
            required: true,
        },
        hash: {
            type: String,
            required: true,
        },
        deadlineId: {
            type: Number,
            default: null,
        },
        selectedDeadline: {
            type: Object,
            default: null,
        },
    },
    emits: ['show-stripe-payment', 'change-deadline'],
    data() {
        return {
            cgvAccepted: false,
            cgvError: false,
            selectedPaymentMethod: null,
            availablePaymentMethods: [],
            loading: true,
            hasError: false,
        };
    },
    computed: {
        hasCgv() {
            return !!this.data.condition;
        },
        hasDeadlines() {
            return this.data.deadlines?.length > 0;
        },
        displayAmount() {
            return this.selectedDeadline
                ? this.selectedDeadline.amount_due
                : this.data.document.amount_due;
        },
        paymentProviderError() {
            return this.data.payments?.error || null;
        },
    },
    created() {
        this.fetchPaymentMethods();
    },
    methods: {
        fetchPaymentMethods() {
            const config = { hash: this.hash };
            if (this.deadlineId && this.deadlineId > 0) {
                config.deadlineId = this.deadlineId;
            }
            this.$ajax
                .post('/quickpay/payment-methods', {}, config)
                .then((response) => {
                    this.availablePaymentMethods = PAYMENT_METHODS.filter(
                        (type) => response.data[type] !== undefined,
                    ).map(function (type) {
                        return { type: type, data: response.data[type] };
                    });
                    if (this.availablePaymentMethods.length > 0) {
                        this.selectedPaymentMethod = this.availablePaymentMethods[0].type;
                    }
                })
                .catch(() => {
                    this.hasError = true;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        handleCgvChange(value) {
            this.cgvAccepted = value;
            if (value) {
                this.cgvError = false;
            }
        },
        handleSelectPaymentMethod(paymentType) {
            this.selectedPaymentMethod = paymentType;
            this.cgvError = false;
        },
        handlePaymentClick(paymentType, paymentData) {
            if (this.hasCgv && !this.cgvAccepted) {
                this.cgvError = true;
                return;
            }

            if (!this.data.payments) {
                throw new Error('Payment method not available');
            }

            switch (paymentType) {
                case 'paypal':
                    this.$refs.paypalForm[0].submit();
                    break;
                case 'atos':
                    this.$refs.atosForm[0].submit();
                    break;
                case 'stripe':
                    this.$emit('show-stripe-payment');
                    break;
                default:
                    if (paymentData.link) {
                        window.location.href = paymentData.link;
                    } else {
                        throw new Error('Payment method not available');
                    }
            }
        },
        handleChangeDeadline() {
            this.$emit('change-deadline');
        },
    },
};
</script>

<style lang="scss" scoped>
.quickpay-payment-overview {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: var(--spacing-3);
    padding: var(--spacing-1);
    &__download-document {
        @media (min-width: 1024px) {
            display: none;
        }
    }
    &__provider-error {
        width: 100%;
    }
    &__cards {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-1);
        width: 100%;
    }
    &__skeleton-card {
        max-width: 400px;
        width: 100%;

        :deep(.compass-skeleton__line) {
            width: 100%;
            height: 52px;
        }

        &--first {
            :deep(.compass-skeleton__line) {
                height: 140px;
            }
        }
    }
}
</style>
